/**
 * AgentMarketplacePage.jsx — Pre-built Agent Marketplace
 * ========================================================
 * Browse, inspect, and install pre-built AI agents organized by
 * SOC role, expertise level, and security domain.
 *
 * v1.2.0 — Connector flexibility + Workflow visibility in Skills tab
 */
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlatform } from '../contexts/PlatformContext';
import { useAuth } from '../contexts/AuthContext';
import {
  AGENT_ROLE_CATEGORIES,
  EXPERTISE_LEVELS,
  MARKETPLACE_AGENTS,
  CONNECTOR_ALTERNATIVES,
  getAgentStats,
} from '../data/agentMarketplace';
import { useMarketplaceCatalogue } from '../data/marketplaceRegistry';
import { Store } from 'lucide-react';
import PageHeader from '../components/common/PageHeader';
import PageContainer from '../components/common/PageContainer';
/* G94 — re-skin chrome with v2. v1 logic preserved. */
import {
  V2PageShell,
  Pill as V2Pill,
} from '../components/v2';

/* ─── SHARED STYLES ─────────────────────────────────────────────────── */

const cardStyle = {
  background: '#fff',
  border: '1px solid #e7e5e4',
  borderRadius: 14,
  padding: 0,
  transition: 'box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease',
  cursor: 'pointer',
  overflow: 'hidden',
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
};
const inputStyle = {
  width: '100%',
  padding: '9px 14px',
  borderRadius: 8,
  border: '1px solid #e7e5e4',
  fontSize: 13,
  fontFamily: 'inherit',
  outline: 'none',
  boxSizing: 'border-box',
  background: '#fafaf9',
  transition: 'border-color 0.15s, box-shadow 0.15s',
};
const labelStyle = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: '#57534e',
  marginBottom: 6,
  letterSpacing: '0.01em',
};
const sectionHeaderStyle = {
  fontSize: 11,
  fontWeight: 700,
  color: '#a8a29e',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  marginBottom: 12,
  marginTop: 0,
};

/* ─── BADGE COMPONENTS ──────────────────────────────────────────────── */

function TierBadge({ tier }) {
  const colors = {
    starter: { bg: '#dcfce7', fg: '#166534' },
    premium: { bg: '#ede9fe', fg: '#5b21b6' },
    enterprise: { bg: '#fef3c7', fg: '#92400e' },
  };
  const c = colors[tier] || colors.starter;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
      fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
      background: c.bg, color: c.fg, letterSpacing: '0.05em',
    }}>
      {tier}
    </span>
  );
}

function ExpertiseBadge({ expertise }) {
  const level = EXPERTISE_LEVELS.find(l => l.id === expertise);
  if (!level) return null;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
      fontSize: 10, fontWeight: 600, background: level.color + '18', color: level.color,
      letterSpacing: '0.03em',
    }}>
      {level.name}
    </span>
  );
}

function SeverityBadge({ severity }) {
  const colors = {
    critical: { bg: '#fef2f2', fg: '#991b1b' },
    high: { bg: '#fef3c7', fg: '#92400e' },
    medium: { bg: '#ede9fe', fg: '#5b21b6' },
    low: { bg: '#e3f4f1', fg: '#0a766b' },
  };
  const c = colors[severity] || colors.medium;
  return (
    <span style={{
      display: 'inline-block', padding: '1px 6px', borderRadius: 3,
      fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
      background: c.bg, color: c.fg,
    }}>
      {severity}
    </span>
  );
}

function EvalTypeBadge({ type }) {
  const colors = {
    functional: { bg: '#dbeafe', fg: '#1e40af' },
    negative: { bg: '#fef3c7', fg: '#92400e' },
    guardrail: { bg: '#fce7f3', fg: '#9d174d' },
    security: { bg: '#fef2f2', fg: '#991b1b' },
    cost: { bg: '#e3f4f1', fg: '#0a766b' },
  };
  const c = colors[type] || colors.functional;
  return (
    <span style={{
      display: 'inline-block', padding: '1px 6px', borderRadius: 3,
      fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
      background: c.bg, color: c.fg,
    }}>
      {type}
    </span>
  );
}

function SkillTypeBadge({ type }) {
  const colors = {
    standard: { bg: '#f4f4f5', fg: '#52525b', label: 'Standard' },
    connector_skill: { bg: '#dbeafe', fg: '#1e40af', label: 'Connector' },
    workflow_skill: { bg: '#ede9fe', fg: '#5b21b6', label: 'Workflow' },
    policy_skill: { bg: '#fce7f3', fg: '#9d174d', label: 'Policy' },
  };
  const c = colors[type] || colors.standard;
  return (
    <span style={{
      display: 'inline-block', padding: '1px 6px', borderRadius: 3,
      fontSize: 9, fontWeight: 600, background: c.bg, color: c.fg,
    }}>
      {c.label}
    </span>
  );
}

function ConnectorChip({ connectorId }) {
  const { connectors } = useMarketplaceCatalogue();
  const conn = connectors.find(c => c.id === connectorId);
  const name = conn ? conn.name : connectorId;
  const icon = conn ? conn.icon : '🔗';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '3px 8px', borderRadius: 5,
      fontSize: 11, fontWeight: 500, background: '#f4f4f5', color: '#3f3f46',
      border: '1px solid #e4e4e7',
    }}>
      <span style={{ fontSize: 12 }}>{icon}</span> {name}
    </span>
  );
}

/* ─── STAT COUNTER ──────────────────────────────────────────────────── */

function StatCard({ label, value, color }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid #e7e5e4', borderRadius: 12,
      padding: '18px 22px', flex: '1 1 0', minWidth: 120,
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      transition: 'box-shadow 0.2s ease',
    }}>
      <div style={{ fontSize: 26, fontWeight: 750, color: color || '#1c1917', letterSpacing: '-0.02em' }}>{value}</div>
      <div style={{ fontSize: 11, color: '#a8a29e', fontWeight: 500, marginTop: 4 }}>{label}</div>
    </div>
  );
}

/* ─── AGENT DETAIL MODAL ────────────────────────────────────────────── */

function AgentDetailModal({ agent, onClose, onInstall }) {
  const [activeTab, setActiveTab] = useState('overview');
  // Connector selections: { originalConnectorId: selectedAlternativeId }
  const [connectorSelections, setConnectorSelections] = useState({});

  // Backend-registry-driven connector catalogue (replaces static
  // MARKETPLACE_CONNECTORS). `connectorsById` gives O(1) lookups inside the
  // render tree; it rebuilds only when the catalogue changes.
  const { connectors: marketplaceConnectors } = useMarketplaceCatalogue();
  const connectorsById = useMemo(() => {
    const map = new Map();
    marketplaceConnectors.forEach(c => map.set(c.id, c));
    return map;
  }, [marketplaceConnectors]);

  // Build the connector swap map: unique categories used by this agent
  // NOTE: All hooks MUST be called before any early return to satisfy React's rules of hooks
  const connectorCategories = useMemo(() => {
    if (!agent) return [];
    const seen = {};
    agent.linkedConnectors.forEach(cId => {
      const alt = CONNECTOR_ALTERNATIVES[cId];
      if (!alt) return;
      if (!seen[alt.category]) {
        seen[alt.category] = { category: alt.category, defaultId: cId, peers: alt.peers };
      }
    });
    return Object.values(seen);
  }, [agent]);

  // Get all skills + workflows combined for the Skills tab
  const allSkillEntries = useMemo(() => {
    if (!agent) return [];
    const skills = agent.linkedSkills.map(s => ({ ...s, entryType: 'skill' }));
    const workflows = agent.linkedWorkflows.map(w => ({
      id: w.id, name: w.name, type: 'workflow_skill', entryType: 'workflow',
      description: w.description, connector: null,
    }));
    return [...skills, ...workflows];
  }, [agent]);

  if (!agent) return null;

  // Resolve a connector ID through the user's selection
  const resolveConnector = (originalId) => {
    return connectorSelections[originalId] || originalId;
  };

  const totalCapabilities = agent.linkedSkills.length + agent.linkedWorkflows.length;

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'skills', label: `Skills (${totalCapabilities})` },
    { id: 'guardrails', label: `Guardrails (${agent.guardrails.length})` },
    { id: 'evals', label: `Evals (${agent.evals.length})` },
    { id: 'policy', label: 'Policy' },
    { id: 'connectors', label: `Connectors (${agent.linkedConnectors.length})` },
  ];

  const category = AGENT_ROLE_CATEGORIES.find(c => c.id === agent.category);
  const expertise = EXPERTISE_LEVELS.find(l => l.id === agent.expertise);

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(15,23,42,0.5)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backdropFilter: 'blur(4px)',
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 18, width: '90%', maxWidth: 900,
        maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
        boxShadow: '0 24px 80px rgba(0,0,0,0.20), 0 0 0 1px rgba(0,0,0,0.05)',
      }} onClick={e => e.stopPropagation()}>

        {/* ── Header ── */}
        <div style={{
          padding: '24px 28px 18px',
          borderBottom: '1px solid #f5f5f4',
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          background: 'linear-gradient(180deg, #fafaf9 0%, #ffffff 100%)',
        }}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            <div style={{
              width: 56, height: 56, borderRadius: 14,
              background: `linear-gradient(135deg, ${(category?.color || '#8b5cf6')}18 0%, ${(category?.color || '#8b5cf6')}08 100%)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, flexShrink: 0,
              border: `1.5px solid ${category?.color || '#8b5cf6'}20`,
              boxShadow: `0 2px 8px ${(category?.color || '#8b5cf6')}12`,
            }}>
              {agent.icon}
            </div>
            <div>
              <h2 style={{ fontSize: 20, fontWeight: 750, color: '#1c1917', margin: 0, lineHeight: 1.3, letterSpacing: '-0.02em' }}>
                {agent.name}
              </h2>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
                <TierBadge tier={agent.tier} />
                <ExpertiseBadge expertise={agent.expertise} />
                <span style={{ fontSize: 11, color: '#a8a29e' }}>v{agent.version}</span>
                <span style={{ fontSize: 11, color: '#a8a29e' }}>by {agent.vendor}</span>
              </div>
              <p style={{ fontSize: 13, color: '#57534e', margin: '8px 0 0', lineHeight: 1.5 }}>
                {agent.description}
              </p>
            </div>
          </div>
          <button onClick={onClose} style={{
            background: '#f5f5f4', border: 'none', cursor: 'pointer', fontSize: 14,
            color: '#78716c', padding: '6px 10px', lineHeight: 1, borderRadius: 8,
            transition: 'background 0.15s',
          }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#e7e5e4'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#f5f5f4'; }}
          >✕</button>
        </div>

        {/* ── Tabs ── */}
        <div style={{
          display: 'flex', gap: 0, borderBottom: '1px solid #f5f5f4',
          padding: '0 28px', overflowX: 'auto',
          background: '#ffffff',
        }}>
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
              padding: '11px 18px', border: 'none', background: 'none', cursor: 'pointer',
              fontSize: 12, fontWeight: activeTab === tab.id ? 600 : 500,
              color: activeTab === tab.id ? '#1c1917' : '#a8a29e',
              borderBottom: activeTab === tab.id ? '2px solid #1c1917' : '2px solid transparent',
              fontFamily: 'inherit', whiteSpace: 'nowrap',
              transition: 'color 0.15s',
            }}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Tab Content ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 28px 28px' }}>

          {/* OVERVIEW */}
          {activeTab === 'overview' && (
            <div>
              <div style={{ marginBottom: 20 }}>
                <h4 style={sectionHeaderStyle}>About this Agent</h4>
                <p style={{ fontSize: 13, color: '#3f3f46', lineHeight: 1.7, margin: 0 }}>
                  {agent.longDescription}
                </p>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                <div style={{ background: '#fafafa', borderRadius: 8, padding: 14 }}>
                  <div style={{ fontSize: 11, color: '#a1a1aa', fontWeight: 600, marginBottom: 4 }}>ROLE CATEGORY</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: category?.color || '#18181b' }}>
                    {category?.icon} {category?.name}
                  </div>
                </div>
                <div style={{ background: '#fafafa', borderRadius: 8, padding: 14 }}>
                  <div style={{ fontSize: 11, color: '#a1a1aa', fontWeight: 600, marginBottom: 4 }}>EXPERTISE LEVEL</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: expertise?.color || '#18181b' }}>
                    {expertise?.name} — {expertise?.description}
                  </div>
                </div>
                <div style={{ background: '#fafafa', borderRadius: 8, padding: 14 }}>
                  <div style={{ fontSize: 11, color: '#a1a1aa', fontWeight: 600, marginBottom: 4 }}>ESTIMATED SETUP</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#18181b' }}>
                    ~{agent.estimatedSetupMinutes} minutes
                  </div>
                </div>
                <div style={{ background: '#fafafa', borderRadius: 8, padding: 14 }}>
                  <div style={{ fontSize: 11, color: '#a1a1aa', fontWeight: 600, marginBottom: 4 }}>CAPABILITIES</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#18181b' }}>
                    {agent.linkedSkills.length} Skills · {agent.linkedWorkflows.length} Workflows · {agent.guardrails.length} Guardrails · {agent.evals.length} Evals
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: 20 }}>
                <h4 style={sectionHeaderStyle}>Required Connectors</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {agent.requiredConnectors.map((req, i) => (
                    <div key={i} style={{
                      padding: '8px 12px', background: '#e3f4f1', borderRadius: 6,
                      border: '1px solid #fde68a', fontSize: 12, color: '#92400e',
                    }}>
                      <strong>Required:</strong> {req}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 style={sectionHeaderStyle}>Tags</h4>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {agent.tags.map(tag => (
                    <span key={tag} style={{
                      padding: '3px 10px', borderRadius: 12, background: '#f4f4f5',
                      fontSize: 11, color: '#52525b', fontWeight: 500,
                    }}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* SKILLS — unified: skills + workflows together */}
          {activeTab === 'skills' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <h4 style={sectionHeaderStyle}>
                Skills & Workflows ({totalCapabilities})
              </h4>
              {allSkillEntries.map((entry, i) => {
                const resolvedConn = entry.connector ? resolveConnector(entry.connector) : null;
                const alt = entry.connector ? CONNECTOR_ALTERNATIVES[entry.connector] : null;
                const hasPeers = alt && alt.peers.length > 1;

                return (
                  <div key={entry.id} style={{
                    padding: '12px 16px', borderRadius: 8,
                    border: '1px solid #e4e4e7',
                    background: entry.entryType === 'workflow' ? '#f5f3ff' : '#fafafa',
                    borderLeft: entry.entryType === 'workflow' ? '3px solid #8b5cf6' : '3px solid transparent',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                          {entry.entryType === 'workflow' && (
                            <span style={{ fontSize: 14 }}>◩</span>
                          )}
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#18181b' }}>
                            {entry.name}
                          </span>
                        </div>
                        {entry.description && entry.entryType === 'workflow' && (
                          <div style={{
                            fontSize: 11, color: '#6d28d9', background: '#ede9fe', padding: '4px 8px',
                            borderRadius: 4, marginBottom: 6, display: 'inline-block',
                            fontFamily: "'IBM Plex Mono', monospace",
                          }}>
                            {entry.description}
                          </div>
                        )}
                        {resolvedConn && (
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                            <ConnectorChip connectorId={resolvedConn} />
                            {hasPeers && (
                              <select
                                value={resolvedConn}
                                onClick={e => e.stopPropagation()}
                                onChange={e => {
                                  e.stopPropagation();
                                  const newVal = e.target.value;
                                  setConnectorSelections(prev => {
                                    const next = { ...prev };
                                    // Set all skills with same original connector to new selection
                                    agent.linkedSkills.forEach(s => {
                                      if (s.connector && CONNECTOR_ALTERNATIVES[s.connector]?.category === alt.category) {
                                        next[s.connector] = newVal;
                                      }
                                    });
                                    return next;
                                  });
                                }}
                                style={{
                                  padding: '2px 6px', borderRadius: 4, border: '1px solid #d4d4d8',
                                  fontSize: 10, fontWeight: 500, color: '#7c3aed', background: '#faf5ff',
                                  cursor: 'pointer', fontFamily: 'inherit',
                                }}
                              >
                                {alt.peers.map(peerId => {
                                  const peer = connectorsById.get(peerId);
                                  return (
                                    <option key={peerId} value={peerId}>
                                      {peer ? `${peer.icon} ${peer.name}` : peerId}
                                    </option>
                                  );
                                })}
                              </select>
                            )}
                          </div>
                        )}
                      </div>
                      <SkillTypeBadge type={entry.type} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* GUARDRAILS */}
          {activeTab === 'guardrails' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <h4 style={sectionHeaderStyle}>Enforced Guardrails ({agent.guardrails.length})</h4>
              {agent.guardrails.map(gr => (
                <div key={gr.id} style={{
                  padding: '12px 16px', background: '#fafafa', borderRadius: 8,
                  border: '1px solid #e4e4e7',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <SeverityBadge severity={gr.severity} />
                    <span style={{ fontSize: 11, color: '#a1a1aa', fontFamily: "'IBM Plex Mono', monospace" }}>
                      {gr.id}
                    </span>
                  </div>
                  <p style={{ fontSize: 13, color: '#3f3f46', margin: 0, lineHeight: 1.5 }}>{gr.rule}</p>
                </div>
              ))}
            </div>
          )}

          {/* EVALS */}
          {activeTab === 'evals' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <h4 style={sectionHeaderStyle}>Evaluation Tests ({agent.evals.length})</h4>
              {agent.evals.map((ev, i) => (
                <div key={i} style={{
                  padding: '12px 16px', background: '#fafafa', borderRadius: 8,
                  border: '1px solid #e4e4e7',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <EvalTypeBadge type={ev.type} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#18181b' }}>{ev.name}</span>
                  </div>
                  <p style={{ fontSize: 12, color: '#52525b', margin: 0, lineHeight: 1.5 }}>{ev.description}</p>
                </div>
              ))}
            </div>
          )}

          {/* POLICY */}
          {activeTab === 'policy' && (
            <div>
              <h4 style={sectionHeaderStyle}>Agent Policy Configuration</h4>
              <p style={{ fontSize: 12, color: '#71717a', margin: '0 0 16px', lineHeight: 1.5 }}>
                These policies control what the agent can and cannot do autonomously. Modify after installation to match your organization's risk tolerance.
              </p>

              {agent.policyConfig.approvalRequired && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#dc2626', marginBottom: 8 }}>
                    🔐 Actions Requiring Approval
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {agent.policyConfig.approvalRequired.map(action => (
                      <span key={action} style={{
                        padding: '4px 10px', borderRadius: 5, background: '#fef2f2',
                        color: '#991b1b', fontSize: 11, fontWeight: 600, border: '1px solid #fecaca',
                      }}>
                        {action}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {agent.policyConfig.autoApproved && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#166534', marginBottom: 8 }}>
                    ✅ Auto-Approved Actions
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {agent.policyConfig.autoApproved.map(action => (
                      <span key={action} style={{
                        padding: '4px 10px', borderRadius: 5, background: '#dcfce7',
                        color: '#166534', fontSize: 11, fontWeight: 600, border: '1px solid #bbf7d0',
                      }}>
                        {action}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#1e40af', marginBottom: 8 }}>
                  🟢 Allowed Actions
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {agent.policyConfig.allowedActions.map(action => (
                    <span key={action} style={{
                      padding: '4px 10px', borderRadius: 5, background: '#dbeafe',
                      color: '#1e40af', fontSize: 11, fontWeight: 600, border: '1px solid #bfdbfe',
                    }}>
                      {action}
                    </span>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#991b1b', marginBottom: 8 }}>
                  🚫 Blocked Actions
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {agent.policyConfig.blockedActions.map(action => (
                    <span key={action} style={{
                      padding: '4px 10px', borderRadius: 5, background: '#fef2f2',
                      color: '#991b1b', fontSize: 11, fontWeight: 600, border: '1px solid #fecaca',
                      textDecoration: 'line-through',
                    }}>
                      {action}
                    </span>
                  ))}
                </div>
              </div>

              {/* Render remaining policy keys as JSON */}
              {(() => {
                const exclude = ['approvalRequired', 'autoApproved', 'allowedActions', 'blockedActions'];
                const remaining = Object.entries(agent.policyConfig).filter(([k]) => !exclude.includes(k));
                if (remaining.length === 0) return null;
                return (
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#52525b', marginBottom: 8 }}>
                      ⚙️ Additional Policy Settings
                    </div>
                    <pre style={{
                      background: '#fafafa', padding: 14, borderRadius: 8, fontSize: 11,
                      fontFamily: "'IBM Plex Mono', monospace", border: '1px solid #e4e4e7',
                      overflow: 'auto', maxHeight: 200, color: '#3f3f46',
                    }}>
{JSON.stringify(Object.fromEntries(remaining), null, 2)}
                    </pre>
                  </div>
                );
              })()}
            </div>
          )}

          {/* CONNECTORS — with swap dropdowns */}
          {activeTab === 'connectors' && (
            <div>
              <div style={{
                padding: '10px 14px', background: '#f5f3ff', borderRadius: 8,
                border: '1px solid #ede9fe', marginBottom: 16, fontSize: 12, color: '#6d28d9',
              }}>
                <strong>Flexible connectors:</strong> Use the dropdowns below to swap tools within the same category. For example, replace CrowdStrike with SentinelOne as your EDR tool. Changes apply across all skills that use that connector.
              </div>

              <h4 style={sectionHeaderStyle}>Connector Configuration</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {connectorCategories.map(({ category: catName, defaultId, peers }) => {
                  const selectedId = connectorSelections[defaultId] || defaultId;
                  const conn = connectorsById.get(selectedId);
                  const hasPeers = peers.length > 1;
                  return (
                    <div key={catName} style={{
                      padding: '14px 18px', background: '#fafafa', borderRadius: 8,
                      border: hasPeers ? '1px solid #c4b5fd' : '1px solid #e4e4e7',
                    }}>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '28px 1fr 110px 210px',
                        alignItems: 'center',
                        gap: 10,
                      }}>
                        {/* Icon column — fixed 28px */}
                        <span style={{ fontSize: 20, textAlign: 'center' }}>{conn?.icon || '🔗'}</span>

                        {/* Name + vendor column — flexible */}
                        <div style={{ minWidth: 0 }}>
                          <div style={{
                            fontSize: 13, fontWeight: 600, color: '#18181b',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          }}>
                            {conn?.name || selectedId}
                          </div>
                          <div style={{
                            fontSize: 11, color: '#a1a1aa',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          }}>
                            {conn?.vendor || 'Unknown'} · v{conn?.version || '?'} · {conn?.skills?.length || 0} skills
                          </div>
                        </div>

                        {/* Category badge column — fixed 110px */}
                        <div style={{ textAlign: 'right' }}>
                          <span style={{
                            padding: '3px 10px', borderRadius: 5, background: '#f4f4f5',
                            fontSize: 10, fontWeight: 600, color: '#52525b', textTransform: 'uppercase',
                            whiteSpace: 'nowrap',
                          }}>
                            {catName}
                          </span>
                        </div>

                        {/* Dropdown column — fixed 210px */}
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                          {hasPeers ? (
                            <select
                              value={selectedId}
                              onChange={e => {
                                const newVal = e.target.value;
                                setConnectorSelections(prev => {
                                  const next = { ...prev };
                                  agent.linkedConnectors.forEach(cId => {
                                    const a = CONNECTOR_ALTERNATIVES[cId];
                                    if (a && a.category === catName) {
                                      next[cId] = newVal;
                                    }
                                  });
                                  agent.linkedSkills.forEach(s => {
                                    if (s.connector) {
                                      const a = CONNECTOR_ALTERNATIVES[s.connector];
                                      if (a && a.category === catName) {
                                        next[s.connector] = newVal;
                                      }
                                    }
                                  });
                                  return next;
                                });
                              }}
                              style={{
                                padding: '4px 8px', borderRadius: 5, border: '1px solid #c4b5fd',
                                fontSize: 12, fontWeight: 500, color: '#5b21b6', background: '#faf5ff',
                                cursor: 'pointer', fontFamily: 'inherit', width: 200,
                              }}
                            >
                              {peers.map(peerId => {
                                const peer = connectorsById.get(peerId);
                                return (
                                  <option key={peerId} value={peerId}>
                                    {peer ? `${peer.icon} ${peer.name}` : peerId}
                                  </option>
                                );
                              })}
                            </select>
                          ) : (
                            <span style={{
                              fontSize: 11, color: '#a1a1aa', fontStyle: 'italic',
                              width: 200, display: 'inline-block', textAlign: 'center',
                            }}>No alternatives</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div style={{
          padding: '16px 28px', borderTop: '1px solid #e4e4e7',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: '#fafaf9',
        }}>
          <div style={{ fontSize: 12, color: '#a1a1aa' }}>
            {agent.linkedSkills.length} skills · {agent.linkedWorkflows.length} workflows · {agent.linkedConnectors.length} connectors · {agent.guardrails.length} guardrails · {agent.evals.length} evals
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={{
              padding: '8px 20px', borderRadius: 6, border: '1px solid #e4e4e7',
              background: '#fff', color: '#52525b', fontSize: 13, fontWeight: 500,
              cursor: 'pointer', fontFamily: 'inherit',
            }}>
              Close
            </button>
            <button onClick={() => onInstall(agent, connectorSelections)} style={{
              padding: '8px 24px', borderRadius: 6, border: 'none',
              background: '#18181b', color: '#fff', fontSize: 13, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit',
            }}>
              Install Agent
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── INSTALL PROGRESS MODAL ────────────────────────────────────────── */

function InstallProgressModal({ steps, currentStep, agentName, error, onClose, onGoToLifecycle }) {
  if (!steps || steps.length === 0) return null;

  const allDone = currentStep >= steps.length && !error;
  const progressPct = Math.round((currentStep / steps.length) * 100);

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.5)', zIndex: 2000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: '#fff', borderRadius: 14, width: 520, maxHeight: '80vh',
        overflow: 'hidden', boxShadow: '0 24px 80px rgba(0,0,0,0.2)',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ padding: '24px 28px 16px', borderBottom: '1px solid #e4e4e7' }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#18181b' }}>
            {allDone ? '✅ Agent Installed Successfully' : error ? '❌ Installation Error' : '⏳ Installing Agent...'}
          </h3>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: '#71717a' }}>
            {allDone
              ? `${agentName} is ready in the Build phase of Agent Lifecycle.`
              : error
              ? 'An error occurred during installation.'
              : `Setting up ${agentName} with linked skills and configuration...`}
          </p>
        </div>

        {/* Progress Bar */}
        <div style={{ padding: '12px 28px 0' }}>
          <div style={{
            height: 6, borderRadius: 3, background: '#f4f4f5', overflow: 'hidden',
          }}>
            <div style={{
              height: '100%', borderRadius: 3,
              background: error ? '#ef4444' : allDone ? '#22c55e' : '#8b5cf6',
              width: `${progressPct}%`, transition: 'width 0.3s ease',
            }} />
          </div>
          <div style={{ fontSize: 11, color: '#a1a1aa', marginTop: 4, textAlign: 'right' }}>
            {currentStep}/{steps.length} steps
          </div>
        </div>

        {/* Steps List */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 28px 20px' }}>
          {steps.map((step, i) => {
            const isDone = i < currentStep;
            const isCurrent = i === currentStep && !allDone && !error;
            const isFailed = i === currentStep && error;
            return (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 0', borderBottom: '1px solid #fafafa',
                opacity: i > currentStep ? 0.4 : 1,
              }}>
                <span style={{
                  width: 22, height: 22, borderRadius: '50%', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700,
                  flexShrink: 0,
                  background: isDone ? '#dcfce7' : isFailed ? '#fef2f2' : isCurrent ? '#ede9fe' : '#f4f4f5',
                  color: isDone ? '#166534' : isFailed ? '#991b1b' : isCurrent ? '#5b21b6' : '#a1a1aa',
                }}>
                  {isDone ? '✓' : isFailed ? '!' : isCurrent ? '●' : (i + 1)}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: 12, fontWeight: isDone || isCurrent ? 600 : 400,
                    color: isDone ? '#166534' : isFailed ? '#991b1b' : isCurrent ? '#18181b' : '#a1a1aa',
                  }}>
                    {step.label}
                  </div>
                  {step.detail && (
                    <div style={{ fontSize: 10, color: '#a1a1aa', marginTop: 1 }}>{step.detail}</div>
                  )}
                </div>
                {isCurrent && !error && (
                  <span style={{ fontSize: 11, color: '#8b5cf6', fontWeight: 500 }}>In progress...</span>
                )}
              </div>
            );
          })}
        </div>

        {/* Error detail */}
        {error && (
          <div style={{
            margin: '0 28px 16px', padding: '10px 14px', background: '#fef2f2',
            borderRadius: 6, border: '1px solid #fecaca', fontSize: 12, color: '#991b1b',
          }}>
            {error}
          </div>
        )}

        {/* Footer */}
        <div style={{
          padding: '16px 28px', borderTop: '1px solid #e4e4e7',
          display: 'flex', justifyContent: 'flex-end', gap: 8, background: '#fafaf9',
        }}>
          {(allDone || error) && (
            <button onClick={onClose} style={{
              padding: '8px 20px', borderRadius: 6, border: '1px solid #e4e4e7',
              background: '#fff', color: '#52525b', fontSize: 13, fontWeight: 500,
              cursor: 'pointer', fontFamily: 'inherit',
            }}>
              {error ? 'Close' : 'Stay in Marketplace'}
            </button>
          )}
          {allDone && (
            <button onClick={onGoToLifecycle} style={{
              padding: '8px 24px', borderRadius: 6, border: 'none',
              background: '#18181b', color: '#fff', fontSize: 13, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit',
            }}>
              Go to Agent Lifecycle →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── MAIN PAGE COMPONENT ───────────────────────────────────────────── */

export default function AgentMarketplacePage() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const {
    createAgent, addSkill, updateSkill, loadAgents,
    createWorkflow, connectors, loadConnectors,
    createConnectorAssignment, linkSkillConnector,
  } = usePlatform();
  const orgId = session?.orgId;
  const tenantId = session?.tenantId;

  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedExpertise, setSelectedExpertise] = useState('all');
  const [selectedTier, setSelectedTier] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [installedAgents, setInstalledAgents] = useState([]);
  const [showInstallToast, setShowInstallToast] = useState(null);

  // Install progress state
  const [installSteps, setInstallSteps] = useState([]);
  const [installCurrentStep, setInstallCurrentStep] = useState(0);
  const [installAgentName, setInstallAgentName] = useState('');
  const [installError, setInstallError] = useState(null);
  const [showInstallProgress, setShowInstallProgress] = useState(false);

  // Marketplace catalogue from the backend connector-registry. Used below to
  // match marketplace connector IDs → real platform connectors during install.
  const { connectors: marketplaceConnectors } = useMarketplaceCatalogue();

  // Load real connectors on mount so we can match marketplace → platform connectors during install
  useEffect(() => {
    if (orgId && tenantId) {
      loadConnectors({ orgId, tenantId });
    }
  }, [orgId, tenantId, loadConnectors]);

  const stats = useMemo(() => getAgentStats(), []);

  const filteredAgents = useMemo(() => {
    return MARKETPLACE_AGENTS.filter(agent => {
      if (selectedCategory !== 'all' && agent.category !== selectedCategory) return false;
      if (selectedExpertise !== 'all' && agent.expertise !== selectedExpertise) return false;
      if (selectedTier !== 'all' && agent.tier !== selectedTier) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const match = agent.name.toLowerCase().includes(q)
          || agent.description.toLowerCase().includes(q)
          || agent.tags.some(t => t.includes(q));
        if (!match) return false;
      }
      return true;
    });
  }, [selectedCategory, selectedExpertise, selectedTier, searchQuery]);

  const groupedByCategory = useMemo(() => {
    const groups = {};
    filteredAgents.forEach(agent => {
      if (!groups[agent.category]) groups[agent.category] = [];
      groups[agent.category].push(agent);
    });
    return groups;
  }, [filteredAgents]);

  // Helper: find a real connector in the platform that matches a marketplace connector ID
  const findRealConnector = useCallback((marketplaceId) => {
    const mpConn = marketplaceConnectors.find(c => c.id === marketplaceId);
    if (!mpConn || !connectors.length) return null;
    // Match by name (case-insensitive) or by type
    const nameLower = mpConn.name.toLowerCase();
    return connectors.find(c =>
      c.name?.toLowerCase() === nameLower ||
      c.name?.toLowerCase().includes(nameLower) ||
      nameLower.includes(c.name?.toLowerCase() || '')
    ) || null;
  }, [connectors, marketplaceConnectors]);

  const handleInstall = useCallback(async (agent, connSelections = {}) => {
    // Close the detail modal and open the progress modal
    setSelectedAgent(null);
    setInstallError(null);
    setInstallAgentName(agent.name);

    // Resolve connector swap: returns the user's selected alternative or the default
    const resolveConn = (originalId) => connSelections[originalId] || originalId;

    // Build the combined step list (skills + workflows)
    const allEntries = [
      ...agent.linkedSkills.map(s => ({ ...s, entryType: 'skill' })),
      ...agent.linkedWorkflows.map(w => ({
        id: w.id, name: w.name, type: 'workflow_skill', entryType: 'workflow',
        description: w.description, connector: null,
      })),
    ];

    // Build step labels for progress modal
    const steps = [
      { label: `Create agent "${agent.name}"`, detail: `Phase: Build · Type: cognitive · Tier: ${agent.tier}` },
      ...allEntries.map(entry => {
        const resolved = entry.connector ? resolveConn(entry.connector) : null;
        const connName = resolved ? (marketplaceConnectors.find(c => c.id === resolved)?.name || resolved) : null;
        return {
          label: `Add ${entry.entryType}: ${entry.name}`,
          detail: `Type: ${entry.type}${connName ? ` · Connector: ${connName}` : ''}`,
        };
      }),
      { label: 'Link connectors & workflows', detail: 'Assign connectors to agent and link to skills' },
      { label: 'Finalize installation', detail: 'Reload agent list and verify' },
    ];
    setInstallSteps(steps);
    setInstallCurrentStep(0);
    setShowInstallProgress(true);

    try {
      // ── Step 1: Create the agent ──
      const agentPayload = {
        name: agent.name,
        type: 'cognitive',
        phase: 'build',
        status: 'active',
        orgId,
        tenantId,
        icon: agent.icon || '🤖',
      };
      const agentResult = await createAgent(agentPayload);
      if (!agentResult?.ok) {
        setInstallError(agentResult?.error || 'Failed to create agent');
        return;
      }
      const newAgentId = agentResult.data?.id;
      setInstallCurrentStep(1);

      // ── Step 2–N: Create each linked skill AND workflow ──
      const categoryMap = {
        connector_skill: 'integration',
        workflow_skill: 'automation',
        policy_skill: 'compliance',
        standard: 'analysis',
      };

      // Track created skill IDs for post-linking
      const createdSkills = []; // { skillId, entry, resolvedConnectorId }

      for (let i = 0; i < allEntries.length; i++) {
        const entry = allEntries[i];
        const resolvedConnectorMpId = entry.connector ? resolveConn(entry.connector) : null;

        const skillPayload = {
          name: entry.name,
          description: entry.entryType === 'workflow'
            ? `Workflow: ${entry.description || entry.name}`
            : (entry.description || `Pre-built skill from ${agent.name} agent`),
          category: categoryMap[entry.type] || 'analysis',
          riskLevel: 'medium',
          autonomy: 'HUMAN_CONTROLLED',
          inferenceMode: 'PLATFORM_INFERENCE',
          guardrails: {},
          skillType: entry.type || 'standard',
          linkedResourceId: null,
          policyConfig: null,
        };

        const skillResult = await addSkill(newAgentId, skillPayload);
        if (!skillResult?.ok) {
          setInstallError(`Failed to add ${entry.entryType} "${entry.name}": ${skillResult?.error || 'Unknown error'}`);
          return;
        }

        const newSkillId = skillResult.data?.id;
        createdSkills.push({
          skillId: newSkillId,
          entry,
          resolvedConnectorMpId,
        });
        setInstallCurrentStep(i + 2);
      }

      // ── Step N+1: Link workflows and connectors ──
      const linkStepIndex = allEntries.length + 1;
      setInstallCurrentStep(linkStepIndex);

      // A) Create real Workflow entities for workflow_skill entries and link via linkedResourceId
      for (const { skillId, entry } of createdSkills) {
        if (entry.entryType === 'workflow' && skillId) {
          try {
            // Create a workflow in the backend
            const wfPayload = {
              name: entry.name,
              description: entry.description || `Workflow for ${agent.name}`,
              agentId: newAgentId,
              orgId,
              tenantId,
              status: 'draft',
              triggerType: 'manual',
              definition: {
                nodes: [
                  { id: 'start', type: 'start', label: 'Start', position: { x: 100, y: 200 } },
                  { id: 'end', type: 'end', label: 'End', position: { x: 600, y: 200 } },
                ],
                connections: [{ from: 'start', to: 'end' }],
              },
            };
            const wfResult = await createWorkflow(wfPayload);
            if (wfResult?.ok && wfResult.data?.id) {
              // Update the skill to link the workflow via linkedResourceId
              await updateSkill(newAgentId, skillId, { linkedResourceId: wfResult.data.id, skillType: 'workflow_skill' });
            }
          } catch (err) {
            console.warn(`Non-critical: Could not create workflow for "${entry.name}":`, err.message);
          }
        }
      }

      // B) Assign connectors to agent and link to connector_skills
      // First, collect all unique marketplace connector IDs used by this agent (resolved)
      const resolvedConnectorIds = new Set();
      agent.linkedConnectors.forEach(cId => {
        resolvedConnectorIds.add(resolveConn(cId));
      });

      // Map marketplace connector IDs → real connector UUIDs
      const mpIdToRealId = {};
      for (const mpId of resolvedConnectorIds) {
        const realConn = findRealConnector(mpId);
        if (realConn?.id) {
          mpIdToRealId[mpId] = realConn.id;
        }
      }

      // Assign each real connector to the agent
      const assignedConnectorIds = new Set();
      for (const [mpId, realId] of Object.entries(mpIdToRealId)) {
        try {
          await createConnectorAssignment(newAgentId, { connectorId: realId });
          assignedConnectorIds.add(realId);
        } catch (err) {
          console.warn(`Non-critical: Could not assign connector "${mpId}" to agent:`, err.message);
        }
      }

      // Link connectors to their respective connector_skills
      for (const { skillId, entry, resolvedConnectorMpId } of createdSkills) {
        if (entry.type === 'connector_skill' && resolvedConnectorMpId && skillId) {
          const realConnId = mpIdToRealId[resolvedConnectorMpId];
          if (realConnId && assignedConnectorIds.has(realConnId)) {
            try {
              await linkSkillConnector(newAgentId, skillId, {
                connectorId: realConnId,
                purpose: 'data_source',
                config: {},
              });
            } catch (err) {
              console.warn(`Non-critical: Could not link connector to skill "${entry.name}":`, err.message);
            }
          }
        }
      }

      // ── Final step: Reload and mark installed ──
      await loadAgents({ orgId, tenantId });
      setInstallCurrentStep(steps.length);
      setInstalledAgents(prev => [...prev, agent.id]);

    } catch (err) {
      setInstallError(err.message || 'Unexpected error during installation');
    }
  }, [createAgent, addSkill, updateSkill, loadAgents, createWorkflow,
      findRealConnector, createConnectorAssignment, linkSkillConnector, orgId, tenantId, marketplaceConnectors]);

  return (
    <V2PageShell
      title="Agent Marketplace"
      subtitle="Pre-built AI agents ready for protected orchestration. Install, configure, and promote through Build → Decide → Act."
      tags={<V2Pill tone="cyan" size="sm">Build</V2Pill>}
    >

      {/* ── Install Toast ── */}
      {showInstallToast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 2000,
          background: 'linear-gradient(135deg, #1c1917 0%, #292524 100%)',
          color: '#fff', padding: '14px 22px',
          borderRadius: 12, fontSize: 13, fontWeight: 500,
          boxShadow: '0 8px 32px rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{ color: '#4ade80', fontSize: 16 }}>✓</span> {showInstallToast} installed — ready for configuration
        </div>
      )}

      {/* ── Stats Row ── */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 28 }}>
        <StatCard label="Total Agents" value={stats.total} color="#1c1917" />
        <StatCard label="Starter" value={stats.byTier.starter || 0} color="#16a34a" />
        <StatCard label="Premium" value={stats.byTier.premium || 0} color="#7c3aed" />
        <StatCard label="Enterprise" value={stats.byTier.enterprise || 0} color="#d97706" />
        <StatCard label="Installed" value={installedAgents.length} color="#0d9488" />
      </div>

      {/* ── Filter Bar ── */}
      <div style={{
        background: '#fff', border: '1px solid #e7e5e4', borderRadius: 14,
        padding: '18px 22px', marginBottom: 26,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}>
        {/* Search */}
        <div style={{ marginBottom: 14 }}>
          <input
            type="text"
            placeholder="Search agents by name, description, or tag..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{ ...inputStyle, fontSize: 14, padding: '10px 14px' }}
          />
        </div>

        {/* Category Pills */}
        <div style={{ marginBottom: 12 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#a8a29e', marginRight: 10, letterSpacing: '0.06em' }}>ROLE:</span>
          <button
            onClick={() => setSelectedCategory('all')}
            style={{
              padding: '4px 12px', borderRadius: 14, border: '1px solid',
              borderColor: selectedCategory === 'all' ? '#18181b' : '#e4e4e7',
              background: selectedCategory === 'all' ? '#18181b' : '#fff',
              color: selectedCategory === 'all' ? '#fff' : '#52525b',
              fontSize: 11, fontWeight: 500, cursor: 'pointer', marginRight: 4,
              fontFamily: 'inherit',
            }}
          >
            All ({MARKETPLACE_AGENTS.length})
          </button>
          {AGENT_ROLE_CATEGORIES.map(cat => {
            const count = MARKETPLACE_AGENTS.filter(a => a.category === cat.id).length;
            if (count === 0) return null;
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                style={{
                  padding: '4px 12px', borderRadius: 14, border: '1px solid',
                  borderColor: selectedCategory === cat.id ? cat.color : '#e4e4e7',
                  background: selectedCategory === cat.id ? cat.color + '18' : '#fff',
                  color: selectedCategory === cat.id ? cat.color : '#52525b',
                  fontSize: 11, fontWeight: 500, cursor: 'pointer', marginRight: 4,
                  fontFamily: 'inherit',
                }}
              >
                {cat.icon} {cat.name} ({count})
              </button>
            );
          })}
        </div>

        {/* Expertise Pills */}
        <div style={{ marginBottom: 12 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#a8a29e', marginRight: 10, letterSpacing: '0.06em' }}>LEVEL:</span>
          <button
            onClick={() => setSelectedExpertise('all')}
            style={{
              padding: '4px 12px', borderRadius: 14, border: '1px solid',
              borderColor: selectedExpertise === 'all' ? '#18181b' : '#e4e4e7',
              background: selectedExpertise === 'all' ? '#18181b' : '#fff',
              color: selectedExpertise === 'all' ? '#fff' : '#52525b',
              fontSize: 11, fontWeight: 500, cursor: 'pointer', marginRight: 4,
              fontFamily: 'inherit',
            }}
          >
            All
          </button>
          {EXPERTISE_LEVELS.map(level => {
            const count = MARKETPLACE_AGENTS.filter(a => a.expertise === level.id).length;
            if (count === 0) return null;
            return (
              <button
                key={level.id}
                onClick={() => setSelectedExpertise(level.id)}
                style={{
                  padding: '4px 12px', borderRadius: 14, border: '1px solid',
                  borderColor: selectedExpertise === level.id ? level.color : '#e4e4e7',
                  background: selectedExpertise === level.id ? level.color + '18' : '#fff',
                  color: selectedExpertise === level.id ? level.color : '#52525b',
                  fontSize: 11, fontWeight: 500, cursor: 'pointer', marginRight: 4,
                  fontFamily: 'inherit',
                }}
              >
                {level.name} ({count})
              </button>
            );
          })}
        </div>

        {/* Tier Pills */}
        <div>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#a8a29e', marginRight: 10, letterSpacing: '0.06em' }}>TIER:</span>
          {['all', 'starter', 'premium', 'enterprise'].map(tier => {
            const count = tier === 'all' ? MARKETPLACE_AGENTS.length : MARKETPLACE_AGENTS.filter(a => a.tier === tier).length;
            const tierColors = { all: '#18181b', starter: '#16a34a', premium: '#7c3aed', enterprise: '#d97706' };
            return (
              <button
                key={tier}
                onClick={() => setSelectedTier(tier)}
                style={{
                  padding: '4px 12px', borderRadius: 14, border: '1px solid',
                  borderColor: selectedTier === tier ? tierColors[tier] : '#e4e4e7',
                  background: selectedTier === tier ? tierColors[tier] + '18' : '#fff',
                  color: selectedTier === tier ? tierColors[tier] : '#52525b',
                  fontSize: 11, fontWeight: 500, cursor: 'pointer', marginRight: 4,
                  fontFamily: 'inherit', textTransform: 'capitalize',
                }}
              >
                {tier} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Agent Grid ── */}
      {filteredAgents.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: 64, color: '#a8a29e', fontSize: 14,
          background: '#fff', borderRadius: 14, border: '1px dashed #e7e5e4',
        }}>
          <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.4 }}>🔍</div>
          No agents match your filters. Try adjusting your search or filters.
        </div>
      ) : (
        Object.entries(groupedByCategory).map(([catId, agents]) => {
          const cat = AGENT_ROLE_CATEGORIES.find(c => c.id === catId);
          return (
            <div key={catId} style={{ marginBottom: 36 }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16,
                paddingBottom: 10,
              }}>
                <span style={{
                  width: 34, height: 34, borderRadius: 10,
                  background: `linear-gradient(135deg, ${(cat?.color || '#8b5cf6')}15 0%, ${(cat?.color || '#8b5cf6')}08 100%)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 18, border: `1px solid ${(cat?.color || '#8b5cf6')}15`,
                }}>
                  {cat?.icon || '🤖'}
                </span>
                <div>
                  <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1c1917', margin: 0, letterSpacing: '-0.01em' }}>
                    {cat?.name || catId}
                  </h3>
                  <p style={{ fontSize: 11, color: '#a8a29e', margin: 0 }}>{cat?.description}</p>
                </div>
                <span style={{
                  marginLeft: 'auto', fontSize: 11, fontWeight: 600,
                  color: '#a8a29e', background: '#f5f5f4', padding: '3px 10px',
                  borderRadius: 8,
                }}>
                  {agents.length} agent{agents.length !== 1 ? 's' : ''}
                </span>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
                gap: 16,
              }}>
                {agents.map(agent => {
                  const isInstalled = installedAgents.includes(agent.id);
                  return (
                    <div
                      key={agent.id}
                      onClick={() => setSelectedAgent(agent)}
                      style={{
                        ...cardStyle,
                        position: 'relative',
                        borderTop: `3px solid ${cat?.color || '#8b5cf6'}`,
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      {/* Installed indicator */}
                      {isInstalled && (
                        <div style={{
                          position: 'absolute', top: 14, right: 14,
                          background: '#dcfce7', color: '#166534', padding: '3px 10px',
                          borderRadius: 6, fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
                          display: 'flex', alignItems: 'center', gap: 3,
                        }}>
                          ✓ Installed
                        </div>
                      )}

                      <div style={{ padding: '18px 20px' }}>
                        {/* Header */}
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
                          <span style={{
                            width: 42, height: 42, borderRadius: 12,
                            background: `linear-gradient(135deg, ${(cat?.color || '#8b5cf6')}15 0%, ${(cat?.color || '#8b5cf6')}08 100%)`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 22, flexShrink: 0,
                            border: `1px solid ${(cat?.color || '#8b5cf6')}12`,
                          }}>
                            {agent.icon}
                          </span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <h4 style={{
                              fontSize: 14, fontWeight: 700, color: '#1c1917',
                              margin: 0, lineHeight: 1.3, letterSpacing: '-0.01em',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>
                              {agent.name}
                            </h4>
                            <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                              <TierBadge tier={agent.tier} />
                              <ExpertiseBadge expertise={agent.expertise} />
                            </div>
                          </div>
                        </div>

                        {/* Description */}
                        <p style={{
                          fontSize: 12, color: '#52525b', margin: '0 0 12px', lineHeight: 1.5,
                          display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}>
                          {agent.description}
                        </p>

                        {/* Metrics */}
                        <div style={{
                          display: 'flex', gap: 12, fontSize: 11, color: '#a1a1aa', fontWeight: 500,
                          paddingTop: 10, borderTop: '1px solid #f4f4f5',
                        }}>
                          <span>{agent.linkedSkills.length} skills</span>
                          <span>·</span>
                          <span>{agent.linkedWorkflows.length} workflows</span>
                          <span>·</span>
                          <span>{agent.guardrails.length} guardrails</span>
                          <span>·</span>
                          <span>~{agent.estimatedSetupMinutes}m setup</span>
                        </div>

                        {/* Connector chips */}
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 8 }}>
                          {agent.linkedConnectors.slice(0, 4).map(cId => (
                            <ConnectorChip key={cId} connectorId={cId} />
                          ))}
                          {agent.linkedConnectors.length > 4 && (
                            <span style={{
                              padding: '3px 8px', borderRadius: 5, background: '#f4f4f5',
                              fontSize: 11, color: '#a1a1aa', fontWeight: 500,
                            }}>
                              +{agent.linkedConnectors.length - 4} more
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}

      {/* ── Agent Detail Modal ── */}
      {/* ── Agent Detail Modal ── */}
      <AgentDetailModal
        agent={selectedAgent}
        onClose={() => setSelectedAgent(null)}
        onInstall={handleInstall}
      />

      {/* ── Install Progress Modal ── */}
      {showInstallProgress && (
        <InstallProgressModal
          steps={installSteps}
          currentStep={installCurrentStep}
          agentName={installAgentName}
          error={installError}
          onClose={() => setShowInstallProgress(false)}
          onGoToLifecycle={() => {
            setShowInstallProgress(false);
            navigate('/agents');
          }}
        />
      )}
    </V2PageShell>
  );
}
